import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import {
  ArrowPathIcon,
  ClipboardDocumentListIcon,
  StarIcon as StarIconOutline,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Alert } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  ColumnVisibilityMenu,
  DataTable,
  useColumnManager,
} from '@/components/common/DataTable';
import { STAR_ICON_SIZE } from '@/components/common/FavoriteButton';
import { useFavoritedMattersContext } from '@/contexts/FavoritedMattersContext';
import { usePagination } from '@/hooks/usePagination';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import {
  useGetMatterListMetadataQuery,
  useGetMatterListQuery,
} from '@/services/api/mattersService';
import {
  MatterFilterParams,
  MatterListItem,
} from '@/services/types/matter-types';
import { selectUser } from '@/state/selectors/root-selectors';
import { getResults, getTotalCount, toQueryParams } from '@/utils/pagination';

import {
  ApplyTaskTemplateModal,
  BulkAssignCaseManagerModal,
  BulkChangeStatusAction,
} from './bulk-actions';
import { BulkMatterCreateModal } from './BulkMatterCreateModal';
import { MatterMetricsBoxes } from './components/MatterMetricsBoxes';
import {
  getDefaultMatterColumns,
  getMatterColumnDisplayName,
  groupMatterColumnsByCategory,
} from './config/columnConfig';
import {
  getBackendFilterKey,
  getColumnIdFromBackend,
  getOrderingField,
} from './config/filterConfig';
import { ACTIVE_MATTER_STATUSES } from './config/matterStatusConfig';
import { CreateMatterFlowDialog } from './CreateMatterFlowDialog';
import { createMatterColumns, getMatterRowClassName } from './mattersColumns';

export const Matters = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const capture = usePostHogCapture();
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
  const [assignCaseManagerOpen, setAssignCaseManagerOpen] = useState(false);
  const [showFlowDialog, setShowFlowDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const { getFavoritedIds, favoritedCount } = useFavoritedMattersContext();

  const { state, actions, searchInput, setSearchInput } = usePagination({
    defaultLimit: 50,
    defaultSort: '-created_at',
    // No initial filter here - we'll apply it conditionally after metadata loads
  });

  // Build query params, including favorite IDs when filter is active
  const favoriteIds = useMemo(
    () => Array.from(getFavoritedIds()),
    [getFavoritedIds],
  );

  const queryParams = useMemo(() => {
    const baseParams = toQueryParams<MatterFilterParams>(
      state,
    ) as MatterFilterParams;

    if (showFavoritesOnly && favoriteIds.length > 0) {
      return { ...baseParams, id: favoriteIds };
    }

    return baseParams;
  }, [state, showFavoritesOnly, favoriteIds]);

  // Skip query if favorites filter is on but user has no favorites
  const skipQuery = showFavoritesOnly && favoriteIds.length === 0;

  const { data, isLoading, error } = useGetMatterListQuery(queryParams, {
    skip: skipQuery,
  });
  const { data: metadata } = useGetMatterListMetadataQuery();

  // When skipping query (no favorites), return empty results
  const matters = useMemo(
    () => (skipQuery ? [] : getResults(data)),
    [skipQuery, data],
  );
  const totalCount = skipQuery ? 0 : getTotalCount(data);

  // Check if current user is a managing paralegal
  const isManagingParalegal = useMemo(() => {
    if (!currentUser?.id || !metadata?.managing_paralegals) return false;
    return metadata.managing_paralegals.some((p) => p.id === currentUser.id);
  }, [currentUser?.id, metadata?.managing_paralegals]);

  // Apply default Active status filter on first load
  // - For managing paralegals: also filter by their ID
  // - For everyone else: just show active matters
  const hasAppliedInitialFilter = useRef(false);
  useEffect(() => {
    // Only run once when metadata loads
    if (!hasAppliedInitialFilter.current && metadata && currentUser?.id) {
      // Check if no filters are currently applied (user hasn't interacted yet)
      const hasUserFilters = Object.keys(state.filters).length > 0;
      if (!hasUserFilters) {
        if (isManagingParalegal) {
          // Managing paralegal: show their active matters
          actions.setFilters({
            managing_paralegal: [currentUser.id],
            status: ACTIVE_MATTER_STATUSES,
          });
        } else {
          // Non-managing paralegal: show all active matters
          actions.setFilters({
            status: ACTIVE_MATTER_STATUSES,
          });
        }
        hasAppliedInitialFilter.current = true;
      }
    }
  }, [metadata, currentUser?.id, isManagingParalegal, state.filters, actions]);

  // Filter options from metadata API
  const firmOptions = useMemo(
    () =>
      (metadata?.firms ?? []).map((firm) => ({
        label: firm.name,
        value: firm.id,
      })),
    [metadata?.firms],
  );

  const attorneyOptions = useMemo(
    () =>
      (metadata?.attorneys ?? []).map((attorney) => ({
        label: attorney.name,
        value: attorney.id,
      })),
    [metadata?.attorneys],
  );

  const paralegalOptions = useMemo(
    () =>
      (metadata?.managing_paralegals ?? []).map((paralegal) => ({
        label: paralegal.name,
        value: paralegal.id,
      })),
    [metadata?.managing_paralegals],
  );

  const caseManagerOptions = useMemo(
    () =>
      (metadata?.case_managers ?? []).map((caseManager) => ({
        label: caseManager.name,
        value: caseManager.id,
      })),
    [metadata?.case_managers],
  );

  const statusOptions = useMemo(
    () => metadata?.status_options ?? [],
    [metadata?.status_options],
  );

  const matterTypeOptions = useMemo(
    () => metadata?.matter_type_options ?? [],
    [metadata?.matter_type_options],
  );

  const preferredLanguageOptions = useMemo(
    () => metadata?.preferred_language_options ?? [],
    [metadata?.preferred_language_options],
  );

  // Column configuration
  const allMatterColumns = useMemo(
    () =>
      createMatterColumns({
        firmOptions,
        attorneyOptions,
        paralegalOptions,
        caseManagerOptions,
        statusOptions,
        matterTypeOptions,
        preferredLanguageOptions,
      }),
    [
      firmOptions,
      attorneyOptions,
      paralegalOptions,
      caseManagerOptions,
      statusOptions,
      matterTypeOptions,
      preferredLanguageOptions,
    ],
  );

  const getDefaultColumns = useCallback(() => getDefaultMatterColumns(), []);

  const columnManager = useColumnManager({
    allColumns: allMatterColumns,
    getDefaultColumns,
  });

  // Handlers
  const handleCreateMatter = () => setShowFlowDialog(true);

  // Toggle favorites filter with analytics
  const handleToggleFavoritesFilter = useCallback(() => {
    const newValue = !showFavoritesOnly;
    setShowFavoritesOnly(newValue);

    if (newValue) {
      // Clear filters and search to show all favorites
      setSearchInput('');
      actions.setFilters({});
    } else {
      actions.setPage(1);
    }

    capture(
      newValue ? 'favorites_filter_enabled' : 'favorites_filter_disabled',
      {
        page: 'matters',
        favorited_count: favoritedCount,
      },
    );
  }, [showFavoritesOnly, capture, favoritedCount, actions, setSearchInput]);

  // Clear all: removes all filters, search, sorting → blank slate
  const handleClearAll = useCallback(() => {
    setSearchInput('');
    setShowFavoritesOnly(false);
    actions.reset();
  }, [setSearchInput, actions]);

  // Reset to default: restores Active status filter for all users
  // Managing paralegals also get their ID filter
  const handleResetToDefault = useCallback(() => {
    setSearchInput('');
    setShowFavoritesOnly(false);
    if (isManagingParalegal && currentUser?.id) {
      actions.setAll({
        filters: {
          managing_paralegal: [currentUser.id],
          status: ACTIVE_MATTER_STATUSES,
        },
        search: '',
        offset: 0,
        ordering: '-created_at',
      });
    } else {
      actions.setAll({
        filters: {
          status: ACTIVE_MATTER_STATUSES,
        },
        search: '',
        offset: 0,
        ordering: '-created_at',
      });
    }
  }, [setSearchInput, actions, isManagingParalegal, currentUser?.id]);

  const handleRowClick = (matter: MatterListItem) =>
    navigate(`/matters/${matter.id}`);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  const handleFilterChange = (
    columnId: string,
    value: string | string[] | null,
  ) => {
    const backendKey = getBackendFilterKey(columnId);
    const newFilters = { ...state.filters };

    if (value === null || (Array.isArray(value) && value.length === 0)) {
      delete newFilters[backendKey];
    } else {
      newFilters[backendKey] = value;
    }

    actions.setFilters(newFilters);
  };

  const handleDateRangeFilterChange = (
    startKey: string,
    endKey: string,
    startDate?: string,
    endDate?: string,
  ) => {
    const newFilters = { ...state.filters };

    if (startDate) {
      newFilters[startKey] = startDate;
    } else {
      delete newFilters[startKey];
    }

    if (endDate) {
      newFilters[endKey] = endDate;
    } else {
      delete newFilters[endKey];
    }

    actions.setFilters(newFilters);
  };

  const handleSortChange = (columnId: string, direction?: 'asc' | 'desc') => {
    const orderingField = getOrderingField(columnId);
    actions.setOrdering(orderingField, direction);
  };

  // Get the actual ordering field (for sort option highlighting)
  const currentSortField = useMemo(() => {
    return state.ordering.replace('-', '');
  }, [state.ordering]);

  const filterValues = useMemo(() => {
    const result: Record<string, string | string[] | null> = {};

    Object.entries(state.filters).forEach(([backendKey, value]) => {
      const columnId = getColumnIdFromBackend(backendKey);
      if (typeof value === 'boolean' || typeof value === 'number') {
        result[columnId] = String(value);
      } else {
        result[columnId] = value;
      }
      // Also store the raw API key for daterange filters to look up
      // (daterange uses raw keys like 'created_at_after', not column IDs)
      if (backendKey !== columnId) {
        if (typeof value === 'boolean' || typeof value === 'number') {
          result[backendKey] = String(value);
        } else {
          result[backendKey] = value;
        }
      }
    });

    return result;
  }, [state.filters]);

  const renderColumnVisibilityMenu = () => (
    <ColumnVisibilityMenu
      allColumns={columnManager.allColumns}
      visibleColumns={columnManager.visibleColumns}
      onToggleColumn={columnManager.toggleColumn}
      onReorderColumns={columnManager.setVisibleColumns}
      onReset={columnManager.resetToDefault}
      hasChanges={columnManager.hasChanges}
      getDisplayName={getMatterColumnDisplayName}
      groupByCategory={groupMatterColumnsByCategory}
      label="Edit columns"
    />
  );

  // Get selected matter objects for bulk actions
  const selectedMatters = useMemo(() => {
    return matters.filter((m) => selectedRows.has(m.id));
  }, [matters, selectedRows]);

  const handleApplyTemplateSuccess = () => {
    setSelectedRows(new Set());
  };

  const handleAssignCaseManagerSuccess = () => {
    setSelectedRows(new Set());
  };

  const handleBulkStatusComplete = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  const renderBulkChangeStatus = useMemo(
    () => (
      <BulkChangeStatusAction
        selectedMatters={selectedMatters}
        onComplete={handleBulkStatusComplete}
      />
    ),
    [selectedMatters, handleBulkStatusComplete],
  );

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col gap-2 overflow-hidden bg-gray-50 px-10 py-2 dark:bg-zinc-950">
      {error && (
        <div className="p-6">
          <Alert severity="error">
            Failed to load matters. Please try refreshing the page.
          </Alert>
        </div>
      )}

      {!error && (
        <>
          <div className="flex flex-shrink-0 items-center justify-between">
            <h1 className="font-serif text-4xl font-medium text-black dark:text-white">
              Matters
            </h1>

            <MatterMetricsBoxes
              paginationState={state}
              paginationActions={actions}
              metadata={metadata}
              isManagingParalegal={isManagingParalegal}
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <DataTable
              data={matters}
              columns={columnManager.displayColumns}
              getRowKey={(row) => row.id}
              searchEnabled
              searchPlaceholder="Search by matter name"
              searchValue={searchInput}
              onSearchChange={handleSearchChange}
              selectionEnabled
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              bulkActions={[
                {
                  label: 'Change Status',
                  customRender: () => renderBulkChangeStatus,
                },
                {
                  label: 'Apply Task Template',
                  icon: <ClipboardDocumentListIcon className="h-4 w-4" />,
                  onClick: () => setApplyTemplateOpen(true),
                },
                {
                  label: 'Case Manager',
                  onClick: () => setAssignCaseManagerOpen(true),
                },
              ]}
              actions={[
                {
                  label: `Favorites (${favoritedCount})`,
                  icon: showFavoritesOnly ? (
                    <StarIconSolid
                      className={`${STAR_ICON_SIZE} shrink-0 text-amber-400`}
                    />
                  ) : (
                    <StarIconOutline className={`${STAR_ICON_SIZE} shrink-0`} />
                  ),
                  onClick: handleToggleFavoritesFilter,
                  variant: showFavoritesOnly ? 'solid' : 'plain',
                  color: 'blue',
                  collapseAt: 'lg',
                },
                {
                  label: 'Reset to default filters',
                  icon: <ArrowPathIcon className="h-4 w-4 shrink-0" />,
                  onClick: handleResetToDefault,
                  variant: 'plain',
                  collapseAt: 'lg',
                },
                {
                  label: 'Clear all filters',
                  icon: <XMarkIcon className="h-4 w-4 shrink-0" />,
                  onClick: handleClearAll,
                  variant: 'plain',
                  collapseAt: 'lg',
                },
                {
                  customRender: renderColumnVisibilityMenu,
                  label: '',
                },
                {
                  label: 'Add Matter',
                  icon: <PlusIcon className="h-4 w-4" />,
                  onClick: handleCreateMatter,
                  color: 'blue',
                  className: 'bg-primary hover:bg-primary-dark !items-center',
                },
              ]}
              menuActions={[
                {
                  label: 'Copy link to view',
                  onClick: handleCopyLink,
                },
                {
                  label: 'Bulk create matters',
                  onClick: () => setBulkCreateOpen(true),
                },
              ]}
              paginationEnabled
              currentPage={actions.getCurrentPage()}
              totalPages={actions.getTotalPages(totalCount)}
              totalCount={totalCount}
              pageSize={state.limit}
              onPageChange={actions.setPage}
              onPageSizeChange={actions.setLimit}
              onSortChange={handleSortChange}
              sortField={currentSortField}
              sortDirection={state.ordering.startsWith('-') ? 'desc' : 'asc'}
              onFilterChange={handleFilterChange}
              onDateRangeFilterChange={handleDateRangeFilterChange}
              filterValues={filterValues}
              isLoading={isLoading}
              emptyMessage="No matters found"
              onRowClick={handleRowClick}
              getRowClassName={getMatterRowClassName}
            />
          </div>
        </>
      )}

      <BulkMatterCreateModal
        open={bulkCreateOpen}
        onClose={() => setBulkCreateOpen(false)}
      />

      <ApplyTaskTemplateModal
        open={applyTemplateOpen}
        onClose={() => setApplyTemplateOpen(false)}
        selectedMatters={selectedMatters}
        onSuccess={handleApplyTemplateSuccess}
      />

      <BulkAssignCaseManagerModal
        open={assignCaseManagerOpen}
        onClose={() => setAssignCaseManagerOpen(false)}
        selectedMatters={selectedMatters}
        onSuccess={handleAssignCaseManagerSuccess}
      />

      <CreateMatterFlowDialog
        open={showFlowDialog}
        onOpenChange={setShowFlowDialog}
      />
    </div>
  );
};
